import { Injectable, inject } from '@angular/core';
import { AppConfig } from 'client/app/app.config';
import { ApiClientConstant } from '@cureskin/api-client';
import { AiParameterInput } from '../../helper/report-helper/report-helper';
import {
  AiDetectionResult,
  CROWN_DENSITY_TARGET_RANGE,
  PARTITION_WIDTH_TARGET_RANGE,
  SCALP_SEGMENT_CLASS,
  SCALP_SEVERITY_MULTIPLIERS,
  SCALP_VISIBILITY_TARGET_RANGE,
  ScalpCardCopy,
  ScalpSeverityMultipliers,
  ScalpCardState,
  ScalpSeverity,
  ScalpSeverityLanguageCopy,
  ScalpSeverityValue,
} from './concern-detections.constants';
import { scalpSeverityCopy } from '../../static-translations/scalp-severity-copy';

const SCALP_LABEL_SEVERITIES: string[] = [ScalpSeverity.Mild, ScalpSeverity.Moderate, ScalpSeverity.Severe];

@Injectable({
  providedIn: 'root',
})
export class ConcernDetectionsService {
  private readonly appConfig: AppConfig = inject(AppConfig);

  getScalpSegmentDetections(results: AiDetectionResult[]): AiDetectionResult[] {
    return (results || []).filter((result: AiDetectionResult): boolean =>
      result.Condition === this.appConfig.Shared.InstantCheckup.DetectionStatus.DETECTED
      && result.class === SCALP_SEGMENT_CLASS);
  }

  getNormalizedScalpSeverity(detection?: AiDetectionResult): string | undefined {
    const rawSeverity = detection?.severity;
    return rawSeverity
      ? rawSeverity.charAt(0).toUpperCase() + rawSeverity.slice(1).toLowerCase()
      : undefined;
  }

  // Finds a Normal-severity scalp-segment regardless of `Condition`. Normal results can arrive
  // with `Condition: 'Not Detected'`, so they are deliberately not filtered by the DETECTED-only
  // `getScalpSegmentDetections()`.
  getNormalScalpDetection(results: AiDetectionResult[]): AiDetectionResult | undefined {
    return (results || []).find((result: AiDetectionResult): boolean =>
      result.class === SCALP_SEGMENT_CLASS && this.getNormalizedScalpSeverity(result) === ScalpSeverity.Normal);
  }

  // Detections render based on drawable masks alone; severity is optional and only drives the
  // label (the prod API may omit it), so it must not gate whether detections are shown.
  hasDrawableScalpMasks(results: AiDetectionResult[]): boolean {
    return this.getScalpSegmentDetections(results).some((detection: AiDetectionResult): boolean => {
      try {
        if (!detection?.scalp_masks) return false;
        const scalpMasks = JSON.parse(detection.scalp_masks || '[]');
        return Array.isArray(scalpMasks) && scalpMasks.length > 0;
      } catch (error) {
        return false;
      }
    });
  }

  getScalpCardState(params: {
    isHairTopView: boolean,
    results: AiDetectionResult[],
    showNoDetectionCard: boolean,
    hairImageType?: string,
  }): ScalpCardState {
    if (!params.isHairTopView) return ScalpCardState.None;
    if (this.getNormalScalpDetection(params.results)) return ScalpCardState.Normal;
    if (this.hasDrawableScalpMasks(params.results)) return ScalpCardState.Detected;
    if (params.hairImageType === 'FRONT') return ScalpCardState.HairFrontDetected;
    if (params.showNoDetectionCard) return ScalpCardState.NoDetection;
    return ScalpCardState.None;
  }

  isScalpLabelSeverity(severity?: string): boolean {
    return !!severity && SCALP_LABEL_SEVERITIES.includes(severity);
  }

  getScalpCardCopy(params: {
    state: ScalpCardState,
    severity?: string,
    gender?: string,
    language?: string,
  }): ScalpCardCopy | undefined {
    const copy: ScalpSeverityLanguageCopy = scalpSeverityCopy[params.language] || scalpSeverityCopy.en;
    switch (params.state) {
      case ScalpCardState.NoDetection:
        return {
          title: copy.noDetection.title,
          reasonsHeading: copy.noDetection.reasonsHeading,
          reasons: copy.noDetection.reasons,
        };
      case ScalpCardState.HairFrontDetected:
        return {
          title: copy.hairFrontDetected.title,
          description: copy.hairFrontDetected.description,
        };
      case ScalpCardState.Normal:
        return {
          title: copy.titles[ScalpSeverity.Normal],
          description: this.getScalpDescriptions(copy, params.gender)[ScalpSeverity.Normal],
        };
      case ScalpCardState.Detected: {
        const severity = this.toCopySeverity(params.severity);
        return {
          title: copy.titles[severity],
          description: this.getScalpDescriptions(copy, params.gender)[severity],
        };
      }
      default:
        return undefined;
    }
  }

  // Returns null when the detection carries no usable ratios (either ratio 0/missing,
  // per the product rule) — the caller then omits the parameter bars from the card.
  getScalpParameterInputs(params: { gender?: string, detection?: AiDetectionResult, language?: string }): AiParameterInput[] | null {
    const severityRatio = this.toFiniteNumber(params.detection?.severity_ratio);
    const scalpAreaRatio = this.toFiniteNumber(params.detection?.scalp_area_ratio);
    if (!severityRatio || severityRatio < 0 || !scalpAreaRatio || scalpAreaRatio < 0) return null;

    const multipliers = this.getScalpSeverityMultipliers(params.detection);
    const labels = (scalpSeverityCopy[params.language] || scalpSeverityCopy.en).parameterLabels;
    const scalpVisibility: AiParameterInput = {
      name: labels.scalpVisibility,
      valuePercent: this.inflateRatioPercent(scalpAreaRatio * 100, multipliers.ratioInflation),
      targetRange: SCALP_VISIBILITY_TARGET_RANGE,
    };

    if (params.gender === ApiClientConstant.User.Gender.FEMALE) {
      return [
        {
          name: labels.partitionWidth,
          valuePercent: this.inflateRatioPercent(severityRatio * 100, multipliers.ratioInflation),
          targetRange: PARTITION_WIDTH_TARGET_RANGE,
        },
        scalpVisibility,
      ];
    }

    const hairMaskArea = this.toFiniteNumber(params.detection?.hair_mask_area) ?? 0;
    const scalpMaskArea = this.toFiniteNumber(params.detection?.scalp_mask_area) ?? 0;
    const totalMaskArea = hairMaskArea + scalpMaskArea;
    if (totalMaskArea <= 0) return null;

    return [
      {
        name: labels.crownDensity,
        valuePercent: multipliers.crownDensityCoefficient * 100 * (hairMaskArea / totalMaskArea),
        targetRange: CROWN_DENSITY_TARGET_RANGE,
      },
      scalpVisibility,
    ];
  }

  private getScalpSeverityMultipliers(detection?: AiDetectionResult): ScalpSeverityMultipliers {
    const severity = this.getNormalizedScalpSeverity(detection) as ScalpSeverityValue;
    return SCALP_SEVERITY_MULTIPLIERS[severity] ?? SCALP_SEVERITY_MULTIPLIERS[ScalpSeverity.Unknown];
  }

  // Product rule: skip the severity inflation instead of overflowing past 100%
  // (the raw ratio percent itself cannot exceed 100 for a ratio <= 1).
  private inflateRatioPercent(rawPercent: number, inflation: number): number {
    const inflated = rawPercent * inflation;
    return inflated > 100 ? rawPercent : inflated;
  }

  private toFiniteNumber(value?: number | string): number | undefined {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  private getScalpDescriptions(
    copy: ScalpSeverityLanguageCopy,
    gender?: string,
  ): Record<ScalpSeverityValue, string> {
    return gender === ApiClientConstant.User.Gender.FEMALE
      ? copy.descriptions.Female
      : copy.descriptions.Male;
  }

  private toCopySeverity(severity?: string): ScalpSeverityValue {
    return this.isScalpLabelSeverity(severity)
      ? severity as ScalpSeverityValue
      : ScalpSeverity.Unknown;
  }
}
